import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

function Profile() {
  const [profile, setProfile] = useState({
    bio: '',
    age: '',
    location: '',
    photo_url: '',
    name: '',
    gender: '',
    sexual_orientation: '',
    looking_for: '',
    age_range_min: 18,
    age_range_max: 50,
    is_verified: false,
    verification_status: 'unverified',
    preferences: {
      relationship: [],
      lifestyle: [],
      interests: [],
      social: [],
      drinking: [],
      smoking: [],
      exercise: []
    }
  });
  const [photos, setPhotos] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [bioCharCount, setBioCharCount] = useState(0);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    getProfile();
    getPhotos();
  }, []);

  const getProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);

      if (user) {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (data) {
          setProfile({
            ...data,
            gender: data.gender || '',
            sexual_orientation: data.sexual_orientation || '',
            looking_for: data.looking_for || '',
            age_range_min: data.age_range_min || 18,
            age_range_max: data.age_range_max || 50,
            is_verified: data.is_verified || false,
            verification_status: data.verification_status || 'unverified',
            preferences: data.preferences || {
              relationship: [],
              lifestyle: [],
              interests: [],
              social: [],
              drinking: [],
              smoking: [],
              exercise: []
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPhotos = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { data, error } = await supabase
          .from('profile_photos')
          .select('*')
          .eq('user_id', user.id)
          .order('upload_order', { ascending: true });

        if (data) {
          setPhotos(data);
        }
      }
    } catch (error) {
      console.error('Error loading photos:', error);
    }
  };

  const uploadPhoto = async (event) => {
    try {
      setUploading(true);
      
      const file = event.target.files[0];
      if (!file) return;

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(fileName);

      // Add to profile_photos table
      const { error: dbError } = await supabase
        .from('profile_photos')
        .insert({
          user_id: user.id,
          photo_url: data.publicUrl,
          is_primary: photos.length === 0, // First photo is primary
          upload_order: photos.length
        });

      if (dbError) throw dbError;

      // If this is the first photo, also update the main profile
      if (photos.length === 0) {
        setProfile({ ...profile, photo_url: data.publicUrl });
      }

      // Refresh photos
      getPhotos();
      
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Error uploading photo');
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async (photoId, photoUrl) => {
    try {
      // Delete from database
      const { error } = await supabase
        .from('profile_photos')
        .delete()
        .eq('id', photoId);

      if (error) throw error;

      // Delete from storage
      const fileName = photoUrl.split('/').pop();
      await supabase.storage
        .from('profile-photos')
        .remove([`${user.id}/${fileName}`]);

      // Refresh photos
      getPhotos();

      // If this was the primary photo, update profile
      const wasMainPhoto = photoUrl === profile.photo_url;
      if (wasMainPhoto) {
        const remainingPhotos = photos.filter(p => p.id !== photoId);
        const newMainPhoto = remainingPhotos.length > 0 ? remainingPhotos[0].photo_url : '';
        setProfile({ ...profile, photo_url: newMainPhoto });
      }

    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Error deleting photo');
    }
  };

  const setPrimaryPhoto = async (photoId, photoUrl) => {
    try {
      // Reset all photos to non-primary
      await supabase
        .from('profile_photos')
        .update({ is_primary: false })
        .eq('user_id', user.id);

      // Set selected photo as primary
      await supabase
        .from('profile_photos')
        .update({ is_primary: true })
        .eq('id', photoId);

      // Update main profile photo
      setProfile({ ...profile, photo_url: photoUrl });
      
      // Refresh photos
      getPhotos();

    } catch (error) {
      console.error('Error setting primary photo:', error);
    }
  };

  const saveProfile = async () => {
  try {
    // Validate required fields
    if (!profile.name || profile.name.trim() === '') {
      alert('Please enter your name before saving your profile.');
      return;
    }

    if (!profile.age || profile.age === '') {
      alert('Please enter your age before saving your profile.');
      return;
    }

    if (!profile.gender || profile.gender === '') {
      alert('Please select your gender before saving your profile.');
      return;
    }

    if (!profile.looking_for || profile.looking_for === '') {
      alert('Please specify who you\'re looking for before saving your profile.');
      return;
    }

    // Ensure age range is set with defaults if not specified
    const ageRangeMin = profile.age_range_min || 18;
    const ageRangeMax = profile.age_range_max || 50;

    // Validate age range makes sense
    if (ageRangeMin > ageRangeMax) {
      alert('Your minimum age preference cannot be higher than your maximum age preference.');
      return;
    }

    // First check if profile exists
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', user.id)
      .single();

    if (existingProfile) {
      // Update existing profile
      const { error } = await supabase
        .from('profiles')
        .update({
          name: profile.name.trim(),
          bio: profile.bio,
          age: profile.age,
          location: profile.location,
          photo_url: profile.photo_url,
          gender: profile.gender,
          sexual_orientation: profile.sexual_orientation,
          looking_for: profile.looking_for,
          age_range_min: ageRangeMin,
          age_range_max: ageRangeMax,
          preferences: profile.preferences,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;
    } else {
      // Insert new profile
      const { error } = await supabase
        .from('profiles')
        .insert({
          id: user.id,
          email: user.email,
          name: profile.name.trim(),
          bio: profile.bio,
          age: profile.age,
          location: profile.location,
          photo_url: profile.photo_url,
          gender: profile.gender,
          sexual_orientation: profile.sexual_orientation,
          looking_for: profile.looking_for,
          age_range_min: ageRangeMin,
          age_range_max: ageRangeMax,
          preferences: profile.preferences,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;
    }

    alert('Profile saved successfully!');
  } catch (error) {
    console.error('Error saving profile:', error);
    alert('Error saving profile');
  }
};

  // Preference options
  const preferenceOptions = {
    relationship: ['Casual', 'Serious', 'Friends', 'Open to anything'],
    lifestyle: ['Party person', 'Homebody', 'Adventurous', 'Calm'],
    interests: ['Netflix', 'Hiking', 'Gaming', 'Reading', 'Music', 'Sports', 'Travel', 'Cooking'],
    social: ['Introvert', 'Extrovert', 'Ambivert'],
    drinking: ['Never', 'Socially', 'Regularly', "Don't mind"],
    smoking: ['Never', 'Socially', 'Regularly', "Don't mind"],
    exercise: ['Daily', 'Weekly', 'Rarely', 'Never']
  };

  // Handle preference selection
  const handlePreferenceChange = (category, option) => {
    const currentPrefs = profile.preferences[category] || [];
    const newPrefs = currentPrefs.includes(option) 
      ? currentPrefs.filter(item => item !== option)
      : [...currentPrefs, option];
    
    setProfile({
      ...profile,
      preferences: {
        ...profile.preferences,
        [category]: newPrefs
      }
    });
  };

  // Handle bio change with character limit
  const handleBioChange = (e) => {
    const text = e.target.value;
    
    if (text.length <= 50) {
      setProfile({ ...profile, bio: text });
      setBioCharCount(text.length);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        color: 'white',
        fontSize: '18px'
      }}>
        Loading your profile...
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '20px 0'
    }}>
      <div style={{ 
        maxWidth: '500px', 
        margin: '0 auto', 
        padding: '0 20px'
      }}>
        {/* Header */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '30px',
          paddingTop: '20px'
        }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '700',
            background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px'
          }}>
            Your Profile
          </h1>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '16px',
            margin: 0
          }}>
            Make your profile shine ✨
          </p>
        </div>

        {/* Account Info Section */}
        <div style={{
          background: 'rgba(0, 233, 255, 0.1)',
          borderRadius: '15px',
          padding: '20px',
          marginBottom: '30px',
          border: '1px solid rgba(0, 233, 255, 0.3)'
        }}>
          <h3 style={{
            color: '#00E9FF',
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '15px',
            textAlign: 'center'
          }}>
            📧 Account Information
          </h3>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            <p style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '16px',
              textAlign: 'center',
              margin: 0
            }}>
              <strong>Email:</strong> {user?.email || profile.email || 'Email not available'}
            </p>
            {profile.user_id && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '10px',
                padding: '10px',
                textAlign: 'center'
              }}>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '14px',
                  margin: '0 0 5px 0'
                }}>
                  <strong>🆔 Your User ID:</strong>
                </p>
                <div style={{
                  fontFamily: 'monospace',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#00E9FF',
                  letterSpacing: '2px'
                }}>
                  {profile.user_id}
                </div>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '12px',
                  margin: '5px 0 0 0'
                }}>
                  Share this ID with friends so they can add you
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Verification Status Section */}
        <div style={{
          background: profile.is_verified 
            ? 'rgba(40, 167, 69, 0.1)' 
            : 'rgba(255, 193, 7, 0.1)',
          borderRadius: '15px',
          padding: '20px',
          marginBottom: '30px',
          border: `1px solid ${profile.is_verified ? 'rgba(40, 167, 69, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '10px'
          }}>
            <h3 style={{
              color: profile.is_verified ? '#28a745' : '#ffc107',
              fontSize: '18px',
              fontWeight: '600',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              {profile.is_verified ? '✅ Verified Profile' : '⚠️ Unverified Profile'}
            </h3>
            
            {!profile.is_verified && (
              <a 
                href="/verify-face" 
                style={{
                  padding: '8px 16px',
                  background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Poppins, sans-serif'
                }}
              >
                Get Verified
              </a>
            )}
          </div>
          
          <p style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '14px',
            margin: 0,
            lineHeight: '1.4'
          }}>
            {profile.is_verified 
              ? '🎉 Your profile has been verified! This shows other users you\'re a real person.'
              : profile.verification_status === 'pending'
              ? '⏳ Your verification is being reviewed. We\'ll update you within 24-48 hours.'
              : '🔒 Verify your identity to get a verified badge and increase trust with other users.'
            }
          </p>
        </div>

        {/* Photo Gallery Section */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '40px'
        }}>
          <h3 style={{ 
            color: '#00E9FF',
            fontSize: '20px',
            fontWeight: '600',
            marginBottom: '20px'
          }}>
            📸 Your Photos
          </h3>

          {/* Main Photo Display */}
          <div style={{ 
            position: 'relative',
            display: 'inline-block',
            marginBottom: '20px'
          }}>
            {profile.photo_url ? (
              <img 
                src={profile.photo_url} 
                alt="Main Profile" 
                style={{
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '4px solid #00E9FF',
                  boxShadow: '0 8px 32px rgba(0, 233, 255, 0.3)'
                }}
              />
            ) : (
              <div style={{
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '4px solid #00E9FF',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '16px',
                margin: '0 auto'
              }}>
                📷
              </div>
            )}
            <div style={{
              position: 'absolute',
              bottom: '5px',
              right: '5px',
              background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
              color: 'white',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              1
            </div>
            
            {/* Verified Badge */}
            {profile.is_verified && (
              <div style={{
                position: 'absolute',
                top: '-5px',
                left: '-5px',
                background: 'linear-gradient(45deg, #28a745, #20c997)',
                color: 'white',
                borderRadius: '50%',
                width: '35px',
                height: '35px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: '600',
                border: '2px solid white',
                boxShadow: '0 4px 12px rgba(40, 167, 69, 0.4)',
                zIndex: 10
              }}>
                ✓
              </div>
            )}
          </div>

          {/* Photo Gallery Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: '15px',
            maxWidth: '400px',
            margin: '0 auto 20px auto'
          }}>
            {photos.map((photo, index) => (
              <div key={photo.id} style={{
                position: 'relative',
                aspectRatio: '1',
                borderRadius: '15px',
                overflow: 'hidden',
                border: photo.is_primary ? '3px solid #00E9FF' : '2px solid rgba(255, 255, 255, 0.2)',
                boxShadow: photo.is_primary ? '0 4px 15px rgba(0, 233, 255, 0.3)' : 'none'
              }}>
                <img 
                  src={photo.photo_url}
                  alt={`Photo ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                
                {/* Photo Number */}
                <div style={{
                  position: 'absolute',
                  top: '5px',
                  left: '5px',
                  background: photo.is_primary 
                    ? 'linear-gradient(90deg, #00E9FF, #8A2BE2)' 
                    : 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {index + 1}
                </div>

                {/* Action Buttons */}
                <div style={{
                  position: 'absolute',
                  top: '5px',
                  right: '5px',
                  display: 'flex',
                  gap: '5px'
                }}>
                  {!photo.is_primary && (
                    <button
                      onClick={() => setPrimaryPhoto(photo.id, photo.photo_url)}
                      style={{
                        background: 'rgba(0, 233, 255, 0.8)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        color: 'white',
                        fontSize: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Set as main photo"
                    >
                      ⭐
                    </button>
                  )}
                  <button
                    onClick={() => deletePhoto(photo.id, photo.photo_url)}
                    style={{
                      background: 'rgba(255, 107, 107, 0.8)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      color: 'white',
                      fontSize: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Delete photo"
                  >
                    ❌
                  </button>
                </div>
              </div>
            ))}

            {/* Add Photo Button */}
            {photos.length < 6 && (
              <label style={{
                aspectRatio: '1',
                border: '2px dashed rgba(255, 255, 255, 0.3)',
                borderRadius: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                background: 'rgba(255, 255, 255, 0.05)',
                transition: 'all 0.3s ease',
                fontSize: '24px',
                color: 'rgba(255, 255, 255, 0.7)'
              }}
              onMouseOver={(e) => {
                e.target.style.borderColor = '#00E9FF';
                e.target.style.background = 'rgba(0, 233, 255, 0.1)';
              }}
              onMouseOut={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
              >
                {uploading ? '⏳' : '➕'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={uploadPhoto}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
              </label>
            )}
          </div>

          {/* Upload Instructions */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '10px',
            padding: '15px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxWidth: '400px',
            margin: '0 auto'
          }}>
            <p style={{ 
              margin: 0,
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: '1.4'
            }}>
              📝 <strong>Tips:</strong> Add up to 6 photos • First photo becomes your main photo • 
              Click ⭐ to set a different main photo • Click ❌ to delete
            </p>
          </div>
        </div>

        {/* Form Fields */}
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '20px',
          padding: '30px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          {/* Name Field */}
          <div style={{ marginBottom: '25px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '10px', 
              fontWeight: '600', 
              color: '#00E9FF',
              fontSize: '16px'
            }}>
              👤 Name
            </label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              placeholder="Your name"
              style={{
                width: '100%',
                padding: '15px 20px',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '16px',
                transition: 'border-color 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#00E9FF'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
            />
          </div>

          {/* Age and Location Row */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '20px', 
            marginBottom: '25px' 
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '10px', 
                fontWeight: '600', 
                color: '#00E9FF',
                fontSize: '16px'
              }}>
                🎂 Age
              </label>
              <input
                type="number"
                value={profile.age}
                onChange={(e) => setProfile({ ...profile, age: e.target.value })}
                placeholder="25"
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '16px',
                  transition: 'border-color 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#00E9FF'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '10px', 
                fontWeight: '600', 
                color: '#00E9FF',
                fontSize: '16px'
              }}>
                📍 Location
              </label>
              <input
                type="text"
                value={profile.location}
                onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                placeholder="Dubai"
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '16px',
                  transition: 'border-color 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#00E9FF'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
              />
            </div>
          </div>

          {/* Gender and Sexual Orientation Row */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '20px', 
            marginBottom: '25px' 
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '10px', 
                fontWeight: '600', 
                color: '#00E9FF',
                fontSize: '16px'
              }}>
                ⚧️ Gender
              </label>
              <select
                value={profile.gender}
                onChange={(e) => setProfile({ ...profile, gender: e.target.value })}
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '16px',
                  transition: 'border-color 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#00E9FF'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
              >
                <option value="" style={{ background: '#1a1a2e' }}>Select gender</option>
                <option value="male" style={{ background: '#1a1a2e' }}>Male</option>
                <option value="female" style={{ background: '#1a1a2e' }}>Female</option>
                <option value="non-binary" style={{ background: '#1a1a2e' }}>Non-binary</option>
                <option value="other" style={{ background: '#1a1a2e' }}>Other</option>
                <option value="prefer-not-to-say" style={{ background: '#1a1a2e' }}>Prefer not to say</option>
              </select>
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '10px', 
                fontWeight: '600', 
                color: '#00E9FF',
                fontSize: '16px'
              }}>
                🌈 Sexual Orientation
              </label>
              <select
                value={profile.sexual_orientation}
                onChange={(e) => setProfile({ ...profile, sexual_orientation: e.target.value })}
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '16px',
                  transition: 'border-color 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#00E9FF'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
              >
                <option value="" style={{ background: '#1a1a2e' }}>Select orientation</option>
                <option value="straight" style={{ background: '#1a1a2e' }}>Straight</option>
                <option value="gay" style={{ background: '#1a1a2e' }}>Gay</option>
                <option value="lesbian" style={{ background: '#1a1a2e' }}>Lesbian</option>
                <option value="bisexual" style={{ background: '#1a1a2e' }}>Bisexual</option>
                <option value="pansexual" style={{ background: '#1a1a2e' }}>Pansexual</option>
                <option value="asexual" style={{ background: '#1a1a2e' }}>Asexual</option>
                <option value="questioning" style={{ background: '#1a1a2e' }}>Questioning</option>
                <option value="other" style={{ background: '#1a1a2e' }}>Other</option>
              </select>
            </div>
          </div>

          {/* Looking For and Age Range Row */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '20px', 
            marginBottom: '25px' 
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '10px', 
                fontWeight: '600', 
                color: '#00E9FF',
                fontSize: '16px'
              }}>
                💕 Looking For
              </label>
              <select
                value={profile.looking_for}
                onChange={(e) => setProfile({ ...profile, looking_for: e.target.value })}
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '16px',
                  transition: 'border-color 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#00E9FF'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
              >
                <option value="" style={{ background: '#1a1a2e' }}>Select preference</option>
                <option value="men" style={{ background: '#1a1a2e' }}>Men</option>
                <option value="women" style={{ background: '#1a1a2e' }}>Women</option>
                <option value="everyone" style={{ background: '#1a1a2e' }}>Everyone</option>
              </select>
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '10px', 
                fontWeight: '600', 
                color: '#00E9FF',
                fontSize: '16px'
              }}>
                📅 Age Range
              </label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="number"
                  min="18"
                  max="100"
                  value={profile.age_range_min}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setProfile({ ...profile, age_range_min: isNaN(value) ? 18 : Math.max(18, value) });
                  }}
                  style={{
                    width: '60px',
                    padding: '12px 8px',
                    borderRadius: '8px',
                    border: '2px solid rgba(255, 255, 255, 0.1)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '14px',
                    textAlign: 'center',
                    boxSizing: 'border-box'
                  }}
                />
                <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>to</span>
                <input
                  type="number"
                  min="18"
                  max="100"
                  value={profile.age_range_max}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setProfile({ ...profile, age_range_max: isNaN(value) ? 50 : Math.min(100, value) });
                  }}
                  style={{
                    width: '60px',
                    padding: '12px 8px',
                    borderRadius: '8px',
                    border: '2px solid rgba(255, 255, 255, 0.1)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '14px',
                    textAlign: 'center',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Bio Field */}
          <div style={{ marginBottom: '30px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '10px', 
              fontWeight: '600', 
              color: '#00E9FF',
              fontSize: '16px'
            }}>
              ✍️ About You
            </label>
            <textarea
              value={profile.bio}
              onChange={handleBioChange}
              placeholder="Tell everyone what makes you unique... (max 50 characters)"
              rows={3}
              style={{
                width: '100%',
                padding: '15px 20px',
                borderRadius: '12px',
                border: `2px solid ${bioCharCount < 20 ? 'rgba(255, 193, 7, 0.5)' : 'rgba(255, 255, 255, 0.1)'}`,
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '16px',
                resize: 'vertical',
                minHeight: '80px',
                transition: 'border-color 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#00E9FF'}
              onBlur={(e) => e.target.style.borderColor = bioCharCount < 20 ? 'rgba(255, 193, 7, 0.5)' : 'rgba(255, 255, 255, 0.1)'}
            />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '8px'
            }}>
              <span style={{
                fontSize: '12px',
                color: bioCharCount < 20 ? '#ffc107' : '#28a745'
              }}>
                {bioCharCount < 20 ? '⚠️ Add more details (20+ characters recommended)' : '✅ Perfect length!'}
              </span>
              <span style={{
                fontSize: '12px',
                color: bioCharCount >= 45 ? '#ffc107' : 'rgba(255, 255, 255, 0.7)'
              }}>
                {bioCharCount}/50 characters
              </span>
            </div>
          </div>

          {/* Preferences Section */}
<div style={{ marginBottom: '30px' }}>
  <button
    type="button"
    onClick={() => setShowPreferences(!showPreferences)}
    style={{
      width: '100%',
      background: 'rgba(0, 233, 255, 0.1)',
      border: '2px solid rgba(0, 233, 255, 0.3)',
      borderRadius: '15px',
      padding: '15px 20px',
      color: '#00E9FF',
      fontSize: '18px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontFamily: 'Poppins, sans-serif',
      transition: 'all 0.3s ease'
    }}
    onMouseOver={(e) => {
      e.target.style.background = 'rgba(0, 233, 255, 0.15)';
      e.target.style.borderColor = 'rgba(0, 233, 255, 0.5)';
    }}
    onMouseOut={(e) => {
      e.target.style.background = 'rgba(0, 233, 255, 0.1)';
      e.target.style.borderColor = 'rgba(0, 233, 255, 0.3)';
    }}
  >
    <span>🎯 Your Preferences</span>
    <span style={{ fontSize: '20px' }}>
      {showPreferences ? '▲' : '▼'}
    </span>
  </button>
  
  {showPreferences && (
    <div style={{ 
      marginTop: '20px',
      padding: '20px',
      background: 'rgba(255, 255, 255, 0.03)',
      borderRadius: '15px',
      border: '1px solid rgba(255, 255, 255, 0.1)'
    }}>
      {Object.entries(preferenceOptions).map(([category, options]) => (
        <div key={category} style={{ marginBottom: '25px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '12px', 
            fontWeight: '600', 
            color: '#00E9FF',
            fontSize: '16px',
            textTransform: 'capitalize'
          }}>
            {category === 'relationship' ? '💕 Relationship' :
             category === 'lifestyle' ? '🌟 Lifestyle' :
             category === 'interests' ? '🎪 Interests' :
             category === 'social' ? '👥 Social' :
             category === 'drinking' ? '🍷 Drinking' :
             category === 'smoking' ? '🚬 Smoking' :
             '💪 Exercise'}
          </label>
          
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '10px' 
          }}>
            {options.map((option) => {
              const isSelected = profile.preferences[category]?.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => handlePreferenceChange(category, option)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: `2px solid ${isSelected ? '#00E9FF' : 'rgba(255, 255, 255, 0.2)'}`,
                    background: isSelected 
                      ? 'linear-gradient(90deg, #00E9FF, #8A2BE2)' 
                      : 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontFamily: 'Poppins, sans-serif'
                  }}
                  onMouseOver={(e) => {
                    if (!isSelected) {
                      e.target.style.borderColor = '#00E9FF';
                      e.target.style.background = 'rgba(0, 233, 255, 0.1)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!isSelected) {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  )}
</div>

          {/* Save Button */}
          <button
            onClick={saveProfile}
            style={{
              width: '100%',
              height: '55px',
              border: 'none',
              background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
              color: 'white',
              borderRadius: '15px',
              fontWeight: '700',
              fontSize: '18px',
              cursor: 'pointer',
              fontFamily: 'Poppins, sans-serif',
              transition: 'transform 0.2s ease',
              boxShadow: '0 8px 32px rgba(0, 233, 255, 0.3)'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-3px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            💾 Save Profile
          </button>
        </div>
      </div>
    </div>
  );
}

export default Profile;